import type { Dict } from "../core.js";
import type { partnerWallet as vi } from "../vi/partnerWallet.js";

export const partnerWallet: Dict<typeof vi> = {
  title: "Partner wallets & quotas",
  intro:
    "Tingee bills Bube from a PREPAID TOKEN WALLET (1 token = ₫1, 150 tokens per transaction webhook, token lots expire); when the wallet runs dry Tingee BLOCKS webhooks and customers stop seeing payments in real time. Tingee has no balance API — the hub estimates it from the balance you read on the baas.tingee.vn portal + the webhook requests bube-banktransfer counts.",
  loadFailed: "Could not load partner wallets.",
  sectionTitle: "Token wallet (prepaid)",
  sectionHint:
    "The balance is an ESTIMATE: latest portal reading − webhook requests × price. Enter a new reading whenever you check the portal so the hub calibrates itself (drift).",
  openReport: "See everything in the Partner report",
  "level.blocked": "Blocked",
  "level.critical": "Running out (< 3 days)",
  "level.low": "Low (< 7 days)",
  "level.incomplete": "Missing data",
  "level.watch": "Watch (< 14 days)",
  "level.ok": "Sufficient",
  "level.unknown": "Not measured",
  "issue.no_pricing":
    "No price on the Live credential set bound to bube-banktransfer — add a « Per transaction » price (Tingee: ₫150 per request, whole contract) in the Pricing section.",
  "issue.no_reading": "No balance read from the portal yet — enter the current balance from baas.tingee.vn › Token.",
  "issue.reading_stale": "The portal reading is stale (over 30 days or outside the measurement window) — read it again so the estimate does not drift.",
  "issue.balance_exhausted": "The estimated balance is below the price of one request — Tingee is blocking or about to block webhooks.",
  "issue.delivery_blocked": "Webhooks are not arriving — transactions only come in via reconciliation pulls (usually an empty wallet, 402).",
  "issue.runway_short": "Less than 14 days left at the current traffic.",
  "issue.lots_expiring": "A lot expires before it can be used up — those tokens will be lost.",
  balance: "Estimated balance",
  balanceHint: "From reading {anchor} at {at}: −{requests} requests ({minor}), +{added} top-ups / adjustments, −{expired} expired",
  runway: "Lasts about",
  runwayDays: "{days} days",
  runwayNever: "Never runs out — no billable requests yet",
  exhaustAt: "Runs out around {at}",
  rate: "Forecast rate",
  rateValue: "{used} requests / day",
  rateHint: "The larger of: 7-day average {avg} · last 24 h {last24h}",
  unitPrice: "Price / request",
  noPrice: "No price",
  month: "This month",
  prevMonth: "Last month",
  monthValue: "{requests} requests · {minor}",
  paidThisMonth: "Topped up this month",
  anchorAt: "Balance read at {at}",
  noAnchor: "No reading yet",
  drift: "Drift at the latest reading",
  driftValue: "{drift} (read {read} · hub estimated {estimated})",
  driftHint: "Negative = the partner charged more than the hub counted (another webhook kind is billed, or the price changed); positive = the hub over-counts.",
  expiring: "Expiring soon",
  expiringValue: "{remaining} expires {at} — about {lost} will be lost",
  lots: "Lots with a balance",
  "kind.reading": "Portal reading",
  "kind.topup": "Top-up lot",
  "kind.bonus": "Bonus lot",
  "kind.adjustment": "Adjustment",
  noExpiry: "No expiry",
  "delivery.title": "Webhooks to Bube",
  "delivery.state.blocked": "Blocked",
  "delivery.state.partial": "Partly missed",
  "delivery.state.ok": "Normal",
  "delivery.state.unknown": "Not measured",
  "delivery.detail": "{undelivered} transactions came via reconciliation with no webhook · {push} webhooks in the last {minutes} minutes",
  "delivery.lastPush": "Latest webhook {at}",
  "delivery.reconcileFailed": "Reconciliation pull failed at {at}: {error}",
  "delivery.blockCodes": "Block codes: {codes}",
  "delivery.error": "Could not read bube-banktransfer's metering: {error}",
  "quota.title": "Transaction quotas",
  "quota.none": "No quota has reached 80%.",
  "quota.product": "Bube's monthly plan",
  "quota.row": "{name} · {used} / {quota} transactions",
  "quota.level.warn": "≥ 80%",
  "quota.level.over": "Over",
  "quota.level.ok": "OK",
  "quota.error": "Could not read the partner report to compute quotas: {error}",
  "days.title": "By day (Vietnam time)",
  "days.day": "Day",
  "days.requests": "Billable requests",
  "days.minor": "Tokens",
  "days.other": "Other webhooks",
  "days.otherHint": "Webhooks the hub assumes are not billed (VA status, Bank Link) — for checking drift.",
  "days.pullOnly": "Via reconciliation only",
  "days.pullOnlyHint": "Transactions whose webhook never arrived — the trace of Tingee blocking webhooks.",
  "days.pushLate": "Late webhooks",
  "ledger.title": "Wallet ledger",
  "ledger.show": "Show ledger ({count})",
  "ledger.hide": "Hide ledger",
  "ledger.empty": "No entries yet.",
  "ledger.kind": "Kind",
  "ledger.units": "Tokens",
  "ledger.paid": "Paid",
  "ledger.occurredAt": "Time",
  "ledger.expiresAt": "Expires",
  "ledger.note": "Note",
  "ledger.by": "By",
  "ledger.delete": "Delete",
  "ledger.confirmYes": "Delete",
  "ledger.confirmNo": "Cancel",
  "ledger.confirmDelete": "Delete this entry? (audited)",
  "form.title": "Record in the ledger",
  "form.kind": "Kind",
  "form.units": "Tokens",
  "form.unitsHint": "1 token = ₫1. Adjustments may be negative.",
  "form.paid": "Amount paid (₫)",
  "form.occurredAt": "Time",
  "form.expiresAt": "Expiry (empty = none)",
  "form.note": "Note",
  "form.save": "Record",
  "form.saving": "Recording…",
  "form.saved": "Recorded.",
  "form.hint.reading": "The current balance on baas.tingee.vn › Token — the anchor the hub estimates from.",
  "form.hint.topup": "A token lot just bought (after the reading): tokens, amount paid, the lot's expiry.",
  "form.hint.bonus": "A gifted token lot (promotion) — usually short-lived.",
  "form.hint.adjustment": "Manual correction when the partner debits / credits outside the rules (± tokens).",
  "strip.title": "Partner wallets & quotas",
  "strip.open": "Details",
  "strip.allOk": "Wallets are sufficient, webhooks arrive normally, no quota has reached 80%.",
  "strip.wallet": "{name}: {balance} · {days} left",
  "strip.walletNoDays": "{name}: {balance}",
  "strip.nextExpiry": "lot expires {at}",
};
